import {
  RefObject,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";

import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Typography from "@mui/material/Typography";
import { alpha, lighten, useTheme } from "@mui/material/styles";

import { useSideMenuFlyout } from "./side-menu-flyout.component";

/**
 * A row is "out of view" once the fade covers most of it: each pill sits over
 * ~40px at its edge of the list, so a row whose middle is in that band is not
 * readable.
 */
const CUE_BAND_PX = 22;

export type ScrollDirection = "up" | "down";

/** What is cut off at one edge of the list. */
export type SideMenuOverflowEdge = {
  /** Anything at all is past this edge. */
  hidden: boolean;
  /** Group titles with at least one row past this edge, in menu order. */
  sections: string[];
  /** Rows past this edge: the pill's "14 more". */
  rowCount: number;
  /** Sum of the numeric badges on rows past this edge ("2 unread"). */
  badgeCount: number;
};

export type SideMenuOverflow = {
  above: SideMenuOverflowEdge;
  below: SideMenuOverflowEdge;
};

const CLEAR: SideMenuOverflowEdge = {
  hidden: false,
  sections: [],
  rowCount: 0,
  badgeCount: 0
};
const NONE: SideMenuOverflow = { above: CLEAR, below: CLEAR };

const sameEdge = (a: SideMenuOverflowEdge, b: SideMenuOverflowEdge) =>
  a.hidden === b.hidden &&
  a.rowCount === b.rowCount &&
  a.badgeCount === b.badgeCount &&
  a.sections.join("|") === b.sections.join("|");

/** Where each row sits in the list's own coordinates (0 = top of the content). */
type Geometry = {
  rows: { mid: number; badge: number }[];
  /** Titled groups, by the index of their first and last row. */
  sections: { title: string; first: number; last: number }[];
  viewport: number;
  content: number;
};

/**
 * Reads every row's position once. Scrolling never changes these, so a scroll
 * only needs `scrollTop` and arithmetic; this runs again only when the list's size
 * or rows change, never per scroll event.
 */
function readGeometry(nav: HTMLElement): Geometry {
  const origin = nav.getBoundingClientRect().top - nav.scrollTop;
  const items = Array.from(nav.querySelectorAll<HTMLLIElement>("li"));
  const rows = items.map((li) => {
    const rect = li.getBoundingClientRect();
    return {
      mid: rect.top + rect.height / 2 - origin,
      badge: Number(li.dataset.sideMenuBadge) || 0
    };
  });
  const sections = Array.from(
    nav.querySelectorAll<HTMLElement>("[data-side-menu-section]")
  ).flatMap((section) => {
    const own = section.querySelectorAll("li");
    const title = section.dataset.sideMenuSection;
    if (!title || own.length === 0) return [];
    return [
      {
        title,
        first: items.indexOf(own[0]),
        last: items.indexOf(own[own.length - 1])
      }
    ];
  });
  return {
    rows,
    sections,
    viewport: nav.clientHeight,
    content: nav.scrollHeight
  };
}

function edgeOf(
  geometry: Geometry,
  hidden: boolean,
  isPast: (mid: number) => boolean,
  /** Which row of a group leaves first past this edge. */
  probe: "first" | "last"
): SideMenuOverflowEdge {
  if (!hidden) return CLEAR;
  let rowCount = 0;
  let badgeCount = 0;
  geometry.rows.forEach(({ mid, badge }) => {
    if (isPast(mid)) {
      rowCount += 1;
      badgeCount += badge;
    }
  });
  const sections = geometry.sections
    .filter((section) => isPast(geometry.rows[section[probe]].mid))
    .map((section) => section.title);
  return { hidden: true, sections, rowCount, badgeCount };
}

/** What is past each edge at this scroll position: arithmetic only, no layout reads. */
function overflowAt(geometry: Geometry, scrollTop: number): SideMenuOverflow {
  const bottom = scrollTop + geometry.viewport;
  return {
    // Below: a group is cut off once its LAST row is.
    below: edgeOf(
      geometry,
      bottom < geometry.content - 1,
      (mid) => mid > bottom - CUE_BAND_PX,
      "last"
    ),
    // Above: once its FIRST row is.
    above: edgeOf(
      geometry,
      scrollTop > 1,
      (mid) => mid < scrollTop + CUE_BAND_PX,
      "first"
    )
  };
}

/**
 * How long the list must be still before the counts catch up. A pill appears or
 * goes the moment its edge changes; the "12 more" and the badge sum it shows
 * follow once the scroll settles, since nobody reads a count mid-flick, and that
 * keeps a long scroll to a handful of renders instead of one per row crossed.
 */
const SETTLE_MS = 120;

/**
 * Watches the scrolling list and reports what is cut off at each edge and by how
 * much. Built for scroll performance:
 * - row positions are read once per layout change (`readGeometry`), so a scroll
 *   event costs `scrollTop` and arithmetic, never a layout read per row;
 * - it runs in the browser's own scroll event, which already fires at most once
 *   a frame;
 * - it renders only when an edge appears or goes, and once more when the scroll
 *   settles (`SETTLE_MS`), and keeps each edge's object when it is unchanged so
 *   only that edge's pill re-renders;
 * - the state lives in the cue, so the rows never re-render while it scrolls.
 */
export function useSideMenuOverflow(
  navRef: RefObject<HTMLElement>,
  /** Changes whenever the rows or their badges do, so positions are re-read. */
  contentKey: string
): SideMenuOverflow {
  const [overflow, setOverflow] = useState<SideMenuOverflow>(NONE);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return undefined;
    let geometry = readGeometry(nav);
    let committed = NONE;
    let settle = 0;
    let lastScrollAt = 0;
    // Where the browser reports resizes, positions are re-read only then. Without
    // it, a size check per event stands in (its reads can force a layout, so
    // it is never done where the observer exists).
    const observing = typeof ResizeObserver !== "undefined";

    const refresh = () => {
      if (
        !observing &&
        (nav.scrollHeight !== geometry.content ||
          nav.clientHeight !== geometry.viewport)
      ) {
        geometry = readGeometry(nav);
      }
    };
    const read = () => {
      refresh();
      return overflowAt(geometry, nav.scrollTop);
    };
    const commit = (next: SideMenuOverflow) => {
      // Keep each edge's object when it says the same thing, so a change at one
      // edge re-renders that edge's pill only.
      const above = sameEdge(committed.above, next.above)
        ? committed.above
        : next.above;
      const below = sameEdge(committed.below, next.below)
        ? committed.below
        : next.below;
      if (above === committed.above && below === committed.below) return;
      committed = { above, below };
      setOverflow(committed);
    };
    const settleNow = () => {
      settle = 0;
      commit(read());
    };
    const checkSettled = () => {
      const quietFor = performance.now() - lastScrollAt;
      settle =
        quietFor >= SETTLE_MS
          ? (settleNow(), 0)
          : window.setTimeout(checkSettled, SETTLE_MS - quietFor);
    };
    const onScroll = () => {
      // Per event, only whether an edge appeared or went: two comparisons, no
      // walk over the rows. The counts are worked out when one does, or once
      // the scroll settles.
      refresh();
      const top = nav.scrollTop;
      const aboveHidden = top > 1;
      const belowHidden = top + geometry.viewport < geometry.content - 1;
      if (
        aboveHidden !== committed.above.hidden ||
        belowHidden !== committed.below.hidden
      ) {
        commit(overflowAt(geometry, top));
      }
      // One timer that re-arms itself at most once per `SETTLE_MS`, never one per
      // event. Not `scrollend`: Chrome fires it after every programmatic scroll,
      // keypress and wheel notch, which is not "the person stopped scrolling".
      lastScrollAt = performance.now();
      if (!settle) settle = window.setTimeout(checkSettled, SETTLE_MS);
    };
    const remeasure = () => {
      geometry = readGeometry(nav);
      commit(read());
    };

    commit(read());
    nav.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", remeasure);
    const observer =
      typeof ResizeObserver === "undefined"
        ? undefined
        : new ResizeObserver(remeasure);
    observer?.observe(nav);
    Array.from(nav.children).forEach((child) => observer?.observe(child));
    return () => {
      window.clearTimeout(settle);
      nav.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", remeasure);
      observer?.disconnect();
    };
  }, [navRef, contentKey]);

  return overflow;
}

const toSentence = (titles: string[]) =>
  titles.length <= 1
    ? (titles[0] ?? "")
    : `${titles.slice(0, -1).join(", ")} and ${titles[titles.length - 1]}`;

const toTitleCase = (title: string) =>
  title.charAt(0).toUpperCase() + title.slice(1).toLowerCase();

const COPY = {
  down: { where: "below", verb: "Scroll down", eyebrow: "More below" },
  up: { where: "above", verb: "Scroll up", eyebrow: "More above" }
} as const;

/** The words for one edge: the pill's short label, its spoken name, its card. */
function describeEdge(direction: ScrollDirection, edge: SideMenuOverflowEdge) {
  const copy = COPY[direction];
  const names = edge.sections.map(toTitleCase);
  const count = edge.rowCount;
  const pages = `${count} more ${count === 1 ? "page" : "pages"}`;
  const hasNames = names.length > 0;
  return {
    // Short enough to fit the narrowest menu; the hover card names the groups.
    label: count > 0 ? `${count} more` : "More",
    accessibleName: hasNames
      ? `${copy.verb}: ${pages} in ${toSentence(names)}`
      : `${copy.verb} for more pages`,
    card: {
      eyebrow: copy.eyebrow,
      label: hasNames ? toSentence(names) : pages,
      description: `${hasNames ? `${pages}. ` : ""}${copy.verb}, or click here to see them.`,
      badgeContent: edge.badgeCount || undefined
    }
  };
}

/** The sum of the badges past one edge: beside the label, or on the chevron's corner. */
function CueBadge({
  direction,
  count,
  collapsed
}: {
  direction: ScrollDirection;
  count: number;
  collapsed: boolean;
}) {
  const theme = useTheme();
  const sx = useMemo(
    () => ({
      ...(collapsed
        ? {
            position: "absolute",
            [direction === "down" ? "top" : "bottom"]: -9,
            right: -13
          }
        : { flex: "0 0 auto" }),
      minWidth: 20,
      height: 20,
      px: 0.625,
      boxSizing: "border-box",
      borderRadius: "10px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "12px",
      lineHeight: 1,
      fontWeight: "fontWeightBold",
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.primary.main,
      boxShadow: collapsed ? `0 0 0 2px ${theme.palette.sideMenu.main}` : "none"
    }),
    [theme, collapsed, direction]
  );
  // Always mounted, shown by an inline style: a badge appearing mid-scroll must
  // not be the moment its styles are first written to the page.
  return (
    <Box
      component="span"
      data-testid={
        count > 0 ? `side-menu-scroll-cue-${direction}-badge` : undefined
      }
      sx={sx}
      style={count > 0 ? undefined : { display: "none" }}
    >
      {count > 99 ? "99+" : count}
    </Box>
  );
}

/**
 * The pill's look, built once per theme / state / direction rather than on every
 * render: on a scroll that crosses many rows the pill re-renders with each new
 * count, and rebuilding these styles (colour maths included) was most of that cost.
 */
function usePillSx(direction: ScrollDirection, collapsed: boolean) {
  const theme = useTheme();
  return useMemo(() => {
    const plum = theme.palette.sideMenu.main;
    const sign = direction === "down" ? 1 : -1;
    return {
      wrap: {
        position: "absolute",
        ...(direction === "down" ? { bottom: 8 } : { top: 6 }),
        left: 0,
        right: 0,
        display: "flex",
        justifyContent: "center",
        px: 1,
        zIndex: 2,
        pointerEvents: "none"
      },
      button: {
        pointerEvents: "auto",
        position: "relative",
        maxWidth: "100%",
        height: 30,
        ...(collapsed ? { width: 30, px: 0 } : { pl: 0.75, pr: 1.25 }),
        gap: 0.5,
        borderRadius: "15px",
        color: theme.palette.common.white,
        backgroundColor: lighten(plum, 0.1),
        border: `1px solid ${alpha(theme.palette.common.white, 0.16)}`,
        boxShadow: `0 6px 16px ${alpha(theme.palette.common.black, 0.4)}`,
        transition: "background-color 120ms ease, transform 120ms ease",
        "&:hover": {
          backgroundColor: lighten(plum, 0.16),
          transform: `translateY(${-sign}px)`
        },
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.sideMenu.itemBgcolorSelected}`,
          outlineOffset: "2px"
        }
        // No entrance animation on purpose: a keyframe animation ticks on the
        // main thread every frame it runs, and it ran during the very scroll
        // that made the pill appear (0.3ms a frame for ~1s, measured).
      },
      chevron: { fontSize: 20, flex: "0 0 20px" },
      label: {
        minWidth: 0,
        fontSize: "14px",
        lineHeight: "18px",
        fontWeight: "fontWeightMedium"
      }
    } as const;
  }, [theme, direction, collapsed]);
}

type ScrollCuePillProps = {
  direction: ScrollDirection;
  edge: SideMenuOverflowEdge;
  collapsed: boolean;
  onScroll: (direction: ScrollDirection) => void;
};

/**
 * One edge's pill: a chevron, "14 more", and the sum of any badges hidden past
 * that edge. Its hover card names the groups. In the rail it is a round chevron.
 * Memoised and fed a stable `edge`, so it re-renders only when what it says
 * changes, never because the other edge did. Always mounted; hidden pills are
 * `visibility: hidden`, so they take no clicks or focus and are not announced.
 */
const ScrollCuePill = memo(function ScrollCuePill({
  direction,
  edge,
  collapsed,
  onScroll
}: ScrollCuePillProps) {
  const flyout = useSideMenuFlyout();
  const sx = usePillSx(direction, collapsed);
  const Chevron =
    direction === "down"
      ? KeyboardArrowDownRoundedIcon
      : KeyboardArrowUpRoundedIcon;

  const shown = edge.hidden;
  const words = describeEdge(direction, edge);

  // Mounted from the start and shown by inline style, so the first time a pill
  // appears mid-scroll is never the first time its styles reach the page (that
  // write invalidates every style on the page, and cost a 2-3ms frame).
  return (
    <Box sx={sx.wrap} style={{ visibility: shown ? "visible" : "hidden" }}>
      <ButtonBase
        data-testid={
          shown
            ? direction === "down"
              ? "side-menu-scroll-cue"
              : "side-menu-scroll-cue-up"
            : undefined
        }
        aria-label={words.accessibleName}
        onClick={() => {
          flyout?.hideNow();
          onScroll(direction);
        }}
        onMouseEnter={(event) =>
          flyout?.show(event.currentTarget, {
            ...words.card,
            onActivate: () => onScroll(direction)
          })
        }
        onMouseLeave={() => flyout?.hide()}
        sx={sx.button}
        style={
          !collapsed && edge.badgeCount > 0 ? { paddingRight: 4 } : undefined
        }
      >
        <Chevron sx={sx.chevron} />
        {!collapsed && (
          <Typography component="span" noWrap sx={sx.label}>
            {words.label}
          </Typography>
        )}
        <CueBadge
          direction={direction}
          count={edge.badgeCount}
          collapsed={collapsed}
        />
      </ButtonBase>
    </Box>
  );
});

/**
 * The fade over one edge. Its look is fixed; only its opacity follows the scroll,
 * and that goes in `style` so showing or hiding it never builds a new class.
 */
const ScrollFade = memo(function ScrollFade({
  direction,
  visible
}: {
  direction: ScrollDirection;
  visible: boolean;
}) {
  const theme = useTheme();
  const sx = useMemo(() => {
    const plum = theme.palette.sideMenu.main;
    return {
      position: "absolute",
      ...(direction === "down" ? { bottom: 0 } : { top: 0 }),
      left: 0,
      right: 0,
      height: direction === "down" ? 72 : 64,
      pointerEvents: "none",
      // Above row badges, which MUI gives `z-index: 1`.
      zIndex: 2,
      transition: "opacity 160ms ease",
      // Solid under the pill, so a badge on a row behind it never peeks out.
      background: `linear-gradient(${direction === "down" ? "to top" : "to bottom"}, ${plum} 50%, ${alpha(plum, 0)} 100%)`
    } as const;
  }, [theme, direction]);
  return (
    <Box
      aria-hidden="true"
      data-testid={`side-menu-fade-${direction === "down" ? "bottom" : "top"}`}
      sx={sx}
      style={{ opacity: visible ? 1 : 0 }}
    />
  );
});

type SideMenuScrollCueProps = {
  /** The scrolling list the cues watch and scroll. */
  navRef: RefObject<HTMLElement>;
  /** Changes whenever the rows or their badges do. */
  contentKey: string;
  collapsed: boolean;
};

/** One screen at a time, keeping a row of the old screen in view for context. */
const scrollStep = (nav: HTMLElement) => Math.max(nav.clientHeight - 64, 64);

/**
 * The "there is more" cues (ENG-2535 follow-up), one at each edge of the list:
 * a fade, and a pill that says how many pages are out of view that way ("14
 * more") and carries the count of any badges hidden there, so an unread Chat
 * scrolled past is never missed, above or below. Its hover card names the groups
 * ("Growth, Scribe and Practice"). Clicking it scrolls one screen that way.
 *
 * It owns the overflow state on purpose, and is memoised: while the list scrolls,
 * only this re-renders, never the rows.
 */
function SideMenuScrollCue({
  navRef,
  contentKey,
  collapsed
}: SideMenuScrollCueProps) {
  const overflow = useSideMenuOverflow(navRef, contentKey);
  const flyout = useSideMenuFlyout();
  // A card pinned to a row that scrolled away would point at the wrong row. A
  // native passive listener, and `hideNow` returns at once while no card is up.
  useEffect(() => {
    const nav = navRef.current;
    if (!nav || !flyout) return undefined;
    nav.addEventListener("scroll", flyout.hideNow, { passive: true });
    return () => nav.removeEventListener("scroll", flyout.hideNow);
  }, [navRef, flyout]);
  const onScroll = useCallback(
    (direction: ScrollDirection) => {
      const nav = navRef.current;
      if (!nav) return;
      const step = scrollStep(nav);
      nav.scrollBy?.({
        top: direction === "down" ? step : -step,
        behavior: "smooth"
      });
    },
    [navRef]
  );

  return (
    <>
      <ScrollFade direction="up" visible={overflow.above.hidden} />
      <ScrollFade direction="down" visible={overflow.below.hidden} />
      <ScrollCuePill
        direction="up"
        edge={overflow.above}
        collapsed={collapsed}
        onScroll={onScroll}
      />
      <ScrollCuePill
        direction="down"
        edge={overflow.below}
        collapsed={collapsed}
        onScroll={onScroll}
      />
    </>
  );
}

export default memo(SideMenuScrollCue);
